import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Spinner, SpinnerSize, Separator } from '@fluentui/react';
import { fetchGeneralById } from "store/actions/generalActions";
import { getComments } from "../../../store/actions/commentActions";
import { fetchPresignedUrl } from "../../../store/actions/storageActions";
import { environment } from "../../../environments/environment";

import "../add/AddGeneral.css";

const formatDate = (dateStr) => {
  if (!dateStr) return '';
  const utcStr = dateStr.endsWith('Z') ? dateStr : dateStr + 'Z';
  return new Date(utcStr).toLocaleDateString('en-US', {
    month: '2-digit',
    day: '2-digit',
    year: 'numeric'
  });
};

const priority = [
        { key: 1, text: "1-Critical" },
        { key: 2, text: "2-Urgent" },
        { key: 3, text: "3-High" },
        { key: 4, text: "4-Medium" },
        { key: 5, text: "5-Low"}
    ];

const formatCreatedOn = (dateStr) => {
    if (!dateStr) return '';

        const date = new Date(dateStr + 'Z');

        return date.toLocaleString('en-US', {
            month: '2-digit',
            day: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            hour12: false
        });
};

const renderPerson = (field) => {
    if (!field) return '';
    if (typeof field === "string" || typeof field === "number") return field;
    if (field.value) return field.value;
    return JSON.stringify(field);
};

const renderPeopleArray = (arr) => {
  if (!arr || !Array.isArray(arr) || arr.length === 0) return "-";
  return arr.join(", ");
};

const GeneralDetails = ({ closeDrawer, id = null }) => {
    const dispatch = useDispatch();

    const { detailLoading: loading, result: task, error } = useSelector(state => state.general);
    const { loading: commentLoading, comments } = useSelector((state) => state.comment);

    useEffect(() => {
        if (id) {
            dispatch(fetchGeneralById(id));
            // Load all comments
            dispatch(getComments({
                workItemId: id,
                sort: 'createdAt',
                order: 'desc',
                pageIndex: 0,
                pageSize: 100,
            }));
        }
    }, [id, dispatch]);

    const formatCommentDate = (dateString) => {
        if (!dateString) return 'N/A';
        const date = new Date(dateString);
        return date.toLocaleString('en-US', {
            month: '2-digit',
            day: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            hour12: false
        });
    };

    const handleFileOpen = async (attachment) => {
        if (!attachment) return;

        try {
            const params = {
                bucketName: environment.storage.s3.mediaBucketName,
                key: attachment.key,
                httpVerb: 0,
                contentType: 0
            };

            const presignedUrlResponse = await dispatch(fetchPresignedUrl(params));

            const presignedUrl = presignedUrlResponse?.url ||
                                presignedUrlResponse?.data?.url ||
                                presignedUrlResponse?.data ||
                                presignedUrlResponse;

            if (presignedUrl && typeof presignedUrl === 'string') {
                const response = await fetch(presignedUrl);
                const blob = await response.blob();
                const blobUrl = window.URL.createObjectURL(blob);

                const link = document.createElement('a');
                link.href = blobUrl;
                link.download = attachment.name || 'download';
                link.click();

                window.URL.revokeObjectURL(blobUrl);
            } else {
                console.error('Invalid presigned URL received');
            }
        } catch (error) {
            console.error('Failed to get presigned URL:', error);
        }
    };

    if (loading) {
        return (
            <div className="drawer">
                <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100%" }}>
                    <Spinner label="Loading..." />
                </div>
            </div>
        );
    }

    if (error) {
        return <div className="drawer"><p>Error: {error}</p></div>;
    }

    if (!task) {
        return null;
    }

    return (
        <div className="drawer-backdrop">
            <div className="drawer">
                <div className="drawer-header">
                    <div className="heading">Task Details</div>
                    <button className="close-btn" onClick={closeDrawer}>&times;</button>
                </div>
                <div className="drawer-content">
                    <div className="form-detail">
                        <div className="row">
                            <div className="col-12">
                                <div className="label-text">Title</div>
                                <div style={{
                                    wordBreak: "break-word",     
                                    overflowWrap: "break-word",          
                                }}>{task.title}</div>
                            </div>
                        </div>

                        <div className="row">
                            <div className="col-12">
                                <div className="label-text">Description</div>
                                 <div>{task.description?.trim() ? task.description : '-'}</div>
                            </div>
                        </div>

                        <div className="row">
                            <div className="col-6">
                                <div className="label-text">Responsible</div>
                                <div>{renderPerson(task.responsible)}</div>
                            </div>

                            <div className="col-6">
                                <div className="label-text">Accountable</div>
                                <div>{task.accountable?.length ? renderPeopleArray(task.accountable) : "-"}</div>
                            </div>
                        </div>
                        <div className="row">
                            <div className="col-6">
                                <div className="label-text">Consult</div>
                                <div>{task.consult?.length ? renderPeopleArray(task.consult) : "-"}</div>
                            </div>

                            <div className="col-6">
                                <div className="label-text">Inform</div>
                                 <div>{task.inform?.length ? renderPeopleArray(task.inform) : "-"}</div>
                            </div>
                        </div>

                        <div className="row">
                            <div className="col-6">
                                <div className="label-text">Created On</div>
                                <div>{formatCreatedOn(task.createdOn)}</div>
                            </div>

                            <div className="col-6">
                                <div className="label-text">Status</div>
                                <div>
                                    <div
                                        className={`chip ${task.status === 1 ? "pending" : task.status === 7 ? "in-progress" : task.status === 6 ? "completed" : ""
                                            }`}
                                    >
                                        {task.status === 1 ? "Pending" : task.status === 7 ? "In Progress" : task.status === 6 ? "Completed" : "Unknown"}
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="row">
                            <div className="col-6">
                                <div className="label-text">Start Date</div>
                                <div>{formatDate(task.startDate)}</div>
                            </div>

                            <div className="col-6">
                                <div className="label-text">End Date</div>
                                <div>{formatDate(task.endDate)}</div>
                            </div>
                        </div>

                        

                        <div className="row">
                            <div className="col-6">
                                <div className="label-text">Priority</div>
                                <div>
                                    {
                                        priority.find(p => p.key === task.priority)?.text
                                    }
                                </div>
                            </div>
                            {task.createdBy && (
                                <div className="col-6" style={{ marginBottom: "16px" }}>
                                    <div className="label-text">Created By</div>
                                    <div>{task.createdBy}</div>
                                </div>
                            )}
                            
                        </div>

                        <div className="row">
                            {task.status === 6 && (
                                <div className="col-6">
                                    <div className="label-text">Completed On</div>
                                    <div>{formatDate(task.completedOn)}</div>
                                </div>
                            )}
                        </div>

                        {/* Comments Section */}
                        {comments && comments.length > 0 && (
                            <>
                                <Separator/>
                                <div className="row">
                                    <div className="col-12">
                                        <div style={{ marginBottom: "10px", fontSize: "16px", fontWeight: "600" }}>
                                            Comments
                                        </div>
                                        <div>
                                            {commentLoading ? (
                                                <div className="comments-loading">
                                                    <Spinner size={SpinnerSize.medium} label="Loading comments..." />
                                                </div>
                                            ) : (
                                        <div>
                                            {comments.map((comment, index) => (
                                                <div key={comment.id || index} className="teams-card" style={{ margin: '15px 0px', position: 'relative' }}>
                                                    <div className="form-detail">
                                                        <div className="row">
                                                            <div className="col-6">
                                                                <div className="label-text">Created By</div>
                                                                <div>{comment.createdByName}</div>
                                                            </div>

                                                            <div className="col-6">
                                                                <div className="label-text">Created On</div>
                                                                <div>{formatCommentDate(comment.createdAt)}</div>
                                                            </div>
                                                        </div>

                                                        <div className="row">
                                                            <div className="col-12">
                                                                <div className="label-text">Comment</div>
                                                                <div className="word-break">{comment.comment ? comment.comment : "-"}</div>
                                                            </div>
                                                        </div>

                                                        {comment.attachments && comment.attachments.length > 0 && (
                                                            <div className="comment-attachments">
                                                                <div className="label-text">Attachment</div>
                                                                {comment.attachments.map((attachment, idx) => (
                                                                    <div className="row" key={idx}>
                                                                        <div className="col-12">
                                                                            <div className="upload-text" style={{ marginBottom: '5px' }}>
                                                                                {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
                                                                                <a
                                                                                    href="#"
                                                                                    onClick={(e) => {
                                                                                        e.preventDefault();
                                                                                        handleFileOpen(attachment);
                                                                                    }}
                                                                                    className="attachment-link"
                                                                                    style={{ cursor: 'pointer' }}
                                                                                >
                                                                                    {attachment.name}
                                                                                </a>
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </>
                        )}

                    </div>
                </div>
            </div>
        </div>
    );
};

export default GeneralDetails;
